// =============================================================================
// Superadmin Sidebar - Navigation pour l'espace Superadmin
// Design sombre avec accent ambre/orange
// NOTE: supabase.auth.signOut() usage is intentional (SDK Auth, not data access)
// =============================================================================

import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  ChevronLeft, 
  ChevronRight,
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  Crown,
  Settings,
  Shield,
  LogOut,
  ArrowLeft,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SuperadminSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const NAV_ITEMS = [
  { 
    path: '/superadmin', 
    icon: LayoutDashboard, 
    label: 'Dashboard',
    end: true
  },
  { 
    path: '/superadmin/organizations', 
    icon: Building2, 
    label: 'Organisations',
  },
  { 
    path: '/superadmin/users', 
    icon: Users, 
    label: 'Utilisateurs',
  },
  { 
    path: '/superadmin/plans', 
    icon: Crown, 
    label: 'Plans',
  },
  { 
    path: '/superadmin/subscriptions', 
    icon: CreditCard, 
    label: 'Abonnements',
  },
  { 
    path: '/superadmin/audit-logs', 
    icon: FileText, 
    label: 'Audit Logs',
  },
  { 
    path: '/superadmin/admins', 
    icon: Shield, 
    label: 'Superadmins',
  },
  { 
    path: '/superadmin/settings', 
    icon: Settings, 
    label: 'Configuration',
  },
];

// Colors
const COLORS = {
  bg: '#0F172A',
  bgHover: '#1E293B',
  bgActive: '#F59E0B20',
  border: '#334155',
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
  accent: '#F59E0B', // Amber
};

export function SuperadminSidebar({ collapsed, onToggle }: SuperadminSidebarProps) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success('Déconnexion réussie');
    navigate('/auth');
  };

  const handleBackToApp = () => {
    navigate('/admin/access?tab=members');
  };

  // ─────────────────────────────────────────────────────────────────────────
  // COLLAPSED MODE
  // ─────────────────────────────────────────────────────────────────────────
  if (collapsed) {
    return (
      <aside 
        className="flex flex-col w-16 shrink-0 border-r"
        style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
      >
        {/* Header */}
        <div 
          className="h-16 flex items-center justify-center border-b"
          style={{ borderColor: COLORS.border }}
        >
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ 
              background: 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)'
            }}
          >
            <Shield className="h-5 w-5 text-white" />
          </div>
        </div>

        {/* Expand Button */}
        <div className="p-2 border-b" style={{ borderColor: COLORS.border }}>
          <Button 
            variant="ghost" 
            size="icon" 
            className="w-full h-8"
            style={{ color: COLORS.textSecondary }}
            onClick={onToggle}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Nav Icons */}
        <ScrollArea className="flex-1">
          <div className="flex flex-col items-center py-3 gap-1">
            {NAV_ITEMS.map((item) => {
              if (!item?.icon) return null;
              const Icon = item.icon;

              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.path}
                      end={item.end}
                      className={({ isActive }) => cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center transition-colors",
                        isActive 
                          ? "bg-amber-500/20" 
                          : "hover:bg-slate-800"
                      )}
                    >
                      {({ isActive }) => (
                        <Icon 
                          className="h-5 w-5" 
                          style={{ 
                            color: isActive ? COLORS.accent : COLORS.textSecondary,
                            strokeWidth: isActive ? 2.5 : 2
                          }} 
                        />
                      )}
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent 
                    side="right"
                    style={{ 
                      backgroundColor: '#1E293B', 
                      color: '#FFFFFF', 
                      borderColor: '#334155' 
                    }}
                  >
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="p-2 border-t space-y-1" style={{ borderColor: COLORS.border }}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="w-full h-10"
                onClick={handleBackToApp}
              >
                <ArrowLeft className="h-5 w-5" style={{ color: COLORS.textSecondary }} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" style={{ backgroundColor: '#1E293B', color: '#FFFFFF', borderColor: '#334155' }}>
              Retour à l'application
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="w-full h-10"
                onClick={handleLogout}
              >
                <LogOut className="h-5 w-5" style={{ color: '#EF4444' }} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" style={{ backgroundColor: '#1E293B', color: '#FFFFFF', borderColor: '#334155' }}>
              Déconnexion
            </TooltipContent>
          </Tooltip>
        </div>
      </aside>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // EXPANDED MODE
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <aside 
      className="flex flex-col w-64 shrink-0 border-r"
      style={{ backgroundColor: COLORS.bg, borderColor: COLORS.border }}
    >
      {/* Header */}
      <div 
        className="h-16 flex items-center justify-between px-4 border-b"
        style={{ borderColor: COLORS.border }}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ 
              background: 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)'
            }}
          >
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <span 
              className="font-bold text-sm"
              style={{ color: COLORS.textPrimary }}
            >
              Superadmin
            </span>
            <p className="text-xs" style={{ color: COLORS.textSecondary }}>
              Gestion plateforme
            </p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          className="w-8 h-8 shrink-0"
          style={{ color: COLORS.textSecondary }}
          onClick={onToggle}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1">
        <nav className="p-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            if (!item?.icon) return null;
            const Icon = item.icon;

            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) => cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                  isActive 
                    ? "bg-amber-500/20 text-amber-400 font-medium" 
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                )}
              >
                {({ isActive }) => (
                  <>
                    <Icon 
                      className="h-5 w-5 shrink-0" 
                      style={{ 
                        color: isActive ? COLORS.accent : COLORS.textSecondary,
                        strokeWidth: isActive ? 2.5 : 2
                      }} 
                    />
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="p-3 border-t space-y-2" style={{ borderColor: COLORS.border }}>
        <Button 
          variant="ghost" 
          className="w-full justify-start gap-3 text-slate-300 hover:bg-slate-800 hover:text-white"
          onClick={handleBackToApp}
        >
          <ArrowLeft className="h-5 w-5" style={{ color: COLORS.textSecondary }} />
          <span className="text-sm">Retour à l'app</span>
        </Button>
        <Button 
          variant="ghost" 
          className="w-full justify-start gap-3 text-red-400 hover:bg-red-500/10 hover:text-red-300"
          onClick={handleLogout}
        >
          <LogOut className="h-5 w-5" />
          <span className="text-sm">Déconnexion</span>
        </Button>
      </div>
    </aside>
  );
}
